/* ============================================
   QUOTE DASHBOARD — Order/Balance Cards + Collapse helpers
   Asset: dashboard-order-cards.js

   Extracted from dashboard-main.js on 2026-05-22 as Phase B.1.b of the
   dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js so the RQD bindings
   exist by the time main runs.

   Public API (attached to window.RQD):
     isOrderFullyFulfilled(q)
     applyCollapseState(detailEl, q)
     renderOrderContentsCard(items, fees, opts)
     renderBalanceReconciliationCard(q, isAdmin)
     sectionTitleHtml(label, done)

   Internal-only helpers (no external callers — bare-name calls within this IIFE):
     sectionSlug(title)
     shouldAutoCollapse(sectionId, q)

   Init contract (RQD.initOrderCards called once by main.js at boot):
     esc, money,
     getIsAdmin (closure-getter — isAdmin mutates after auth resolves),
     buildOnlineFlowSteps, isFreightBalanced, vendorNamesMatch,
     collapsedSections (shared object ref — mutated by main.js click handler
       at ~line 1985 + read by activity-card builder at ~line 8480 +
       read by prospect detail at ~line 17097),
     sectionsAutoCollapsedRef (shared object ref — kept for other modules; no longer the gate)
     manuallyToggledSections  (shared object ref — cards the rep toggled by hand; auto-collapse
                               re-evaluates every render and must never override these)

   The two state stores are passed BY REFERENCE so mutations on either side
   stay in sync — main.js still toggles them in click handlers.

   Internal cross-references between extracted functions use BARE names
   (sibling functions in this IIFE — same scope as before extraction).
   Only deps from main.js's IIFE go through the init-deps surface.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initOrderCards (from main.js boot)
  var esc, money;
  var getIsAdmin;
  var buildOnlineFlowSteps, isFreightBalanced, vendorNamesMatch;
  var collapsedSections, sectionsAutoCollapsedRef, manuallyToggledSections;

    // === sectionSlug — extracted from main.js:6614-6616 ===
    // Slugify a section title to a stable identifier used for collapse-state
    // bookkeeping. "AR / AP" → "ar-ap", "Order Contents" → "order-contents".
    function sectionSlug(title) {
      return String(title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-29) — a card may DECLARE its identity instead of
       having it derived from its heading.

       ⛔ WHY THIS EXISTS. Every other card's id is slugged from the title's textContent, which is
       fine while the heading is a fixed label. It stops being fine the moment a heading carries
       live values — and it has to, because a collapsed card hides everything except its title, so
       "collapsed but still readable at a glance" means the numbers live IN the heading. Slugging
       "Freight Quote · ECORE · Estes · $412.00" gives a different id every time the cost changes,
       so the card would forget it was collapsed on each edit and the bug would look intermittent.

       Opt-in and inert for every existing card: with no data-section-id the behaviour is byte for
       byte what it was. The chevron strip stays on the derived path only — a declared id is already
       clean, and running the regex over it would quietly rewrite any id ending in a digit-free
       plus/minus, which is a trap for the next person to declare one. */
    function sectionIdFor(card) {
      var declared = card && card.dataset ? card.dataset.sectionId : '';
      if (declared) return declared;
      var titleEl = card && card.querySelector ? card.querySelector('.rqd-detail__section-title') : null;
      if (!titleEl) return '';
      // sectionSlug reads textContent which would include any chevron we injected on a prior pass,
      // and any summary a declaring card put there. Strip the chevron first.
      return sectionSlug(titleEl.textContent.replace(/[+−]\s*$/, '').trim());
    }

    // === isOrderFullyFulfilled — extracted from main.js:6631-6647 ===
    // Should this card start collapsed for an admin on initial render? The
    // top reference cards (.rqd-detail__card) never get this far — only
    // workflow cards (.rqd-detail__arap) are collapsible. Order Contents
    // and Notes never auto-collapse since they're reference info even down
    // here.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Detect "fully
       fulfilled" — the same signal that lights up the ✓ Fulfilled pill in
       the To Do column for online orders (every flow step .done) or the
       ✓ Closed pill for offline orders (closedStatus === 'closed', which
       the worker only sets when every post-sale obligation is settled).
       When this is true every card under the detail panel auto-collapses,
       including Order Contents + Notes which usually stay open as
       reference info. */
    function isOrderFullyFulfilled(q) {
      if (!q) return false;
      if (q.source === 'online') {
        try {
          var _steps = buildOnlineFlowSteps(q);
          return Array.isArray(_steps) && _steps.length > 0 && _steps.every(function (s) { return s && s.done; });
        } catch (_) {
          return false;
        }
      }
      // Offline: closedStatus is the worker-derived "all post-sale
      // obligations met" gate (vendors ordered, tracking entered, taxes
      // remitted, installer paid, commissions paid, etc.). Cancelled
      // rows don't count as fulfilled — they're a separate end-state.
      if (q.closedStatus === 'closed' && !q.cancelled) return true;
      return false;
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // === shouldAutoCollapse — extracted from main.js:6650-6767 ===
    function shouldAutoCollapse(sectionId, q) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE SAMPLES CARD STARTS COLLAPSED, FOR
         EVERYONE, ALWAYS. Ryan: *"the SAMPLES card should be collapsed by default all the time when
         you expand a row like JW-1263-A… Only some orders end up ordering samples, so no need to
         have it default to being expanded."* Above the admin gate on purpose — reps got every card
         expanded; this one they do not. A manual toggle still sticks: the caller skips sections in
         `manuallyToggledSections` before asking here. The list-but-no-tracking exception further
         down (Ryan's 08-30 rule) is superseded by this line and never reached for these two ids. */
      if (sectionId === 'samples' || sectionId === 'sample-tracking') return true;
      var isAdmin = getIsAdmin ? getIsAdmin() : false;
      if (!isAdmin) return false;
      /* CUSTOM_ADJUSTOPEN_BY_CLAUDE (Ryan 2026-10-06) - ⛔ AN ADJUSTMENT STILL WAITING ON THE CUSTOMER KEEPS THE CARD OPEN,
         sent or not, fulfilled or not: it collapses once the balance is paid. Above the fully-fulfilled sweep on purpose.
         `adjustLiveCollect` is the worker's read of the ledger (a pending collect); PARTIALLY_PAID is Shopify saying
         the same thing, for a row painted from a cache older than the field. This rule is re-run on every render,
         and it used to say "collapse" here, so the card the history fill had just opened shut on the next redraw. */
      if (sectionId === 'adjust-order' && (q.adjustLiveCollect
        || (q.source === 'online' && String(q.onlineFinancialStatus || '').toUpperCase() === 'PARTIALLY_PAID'))) return false;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Fully-fulfilled
         orders collapse EVERY card (including Order Contents + Notes)
         on initial expand. Once the rep clicks a card to expand it,
         their choice sticks via the existing collapsedSections store. */
      if (isOrderFullyFulfilled(q)) return true;
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-13) — the Delay / Backorder
         Notice card is an occasional optional action, not standing reference —
         default it collapsed so it doesn't take up room on every online order. */
      if (sectionId === 'delay-backorder-notice') return true;
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-07-22) — Adjust Order is an
         occasional corrective action, not standing reference → default collapsed,
         like the Sample Tracking / Delay cards. */
      if (sectionId === 'adjust-order') {
        /* CUSTOM_FREIGHTREMINDER_BY_CLAUDE (Ryan 2026-08-29) — ⛔ EXCEPT ON A FREIGHT-QUOTED ORDER
           THAT HAS NOT BEEN CHARGED FREIGHT YET, where it is not occasional at all: it is the next
           thing that has to happen, and the card is where it happens.

           Ryan: *"make it so the ADJUST ORDER card is auto-expanded on freight-quoted orders with
           no freight ... That'll help remind admins when these orders come in of what they need
           to do."*

           ⚠️ $0.01 COUNTS AS NO FREIGHT. Shopify will not accept a $0.00 rate, so the storefront
           charges a one-cent placeholder on these orders and the real number is settled later —
           testing `> 0` would read that penny as "freight has been handled" and hide the card on
           precisely the orders that need it. The threshold is a cent-and-a-half so a stray
           rounding remainder cannot creep past it either.

           ⚠️ Gated on `_isFreightQuoted`, the worker's own signal for "we broker this shipment",
           so a normal parcel order that genuinely ships for a few cents is unaffected.

           ⚠️ AND IT RE-COLLAPSES ON ITS OWN once freight is charged — this returns to `true` the
           moment the number lands, so the reminder disappears when it stops being true rather
           than sitting expanded forever. Admin's own click still wins either way, via
           collapsedSections. */
        var _fCharged = parseFloat((q.financials || {}).freightCharged) || 0;
        if (q._isFreightQuoted && _fCharged <= 0.015) return false;
        return true;
      }
      if (sectionId === 'order-contents' || sectionId === 'notes') return false;
      if (sectionId === 'ar-ap') {
        // Online orders: AR/AP just shows "Paid via Shopify" once paid — done.
        if (q.source === 'online') return true;
        var qt = parseFloat(q.total || q.quoteAmount || 0);
        var paid = (q.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
        if (!(qt > 0 && paid >= qt)) return false;
        var taxesAmt = parseFloat((q.financials || {}).taxes) || 0;
        if (taxesAmt > 0 && !q.taxesRemitted) return false;
        var installCost = parseFloat((q.financials || {}).installCost) || 0;
        if (installCost > 0 && !q.installerPaid) return false;
        return true;
      }
      if (sectionId === 'purchasing') {
        var vendors = (q.financials || {}).vendors || [];
        if (vendors.length === 0) return false;
        var pos = q.pos || [];
        /* CUSTOM_FOVCOSTROW_BY_CLAUDE (Ryan 2026-09-01) — ⛔ AN OUR-CARRIER ORDER OWING US A
           FREIGHT NUMBER IS NOT FINISHED. This collapsed on "every vendor's PO has gone out",
           which on RW-1339-A folded the card away while the Blue Sky carrier cost was still
           unset — Ryan: *"the card should not auto-collapse on page load until the number is
           saved a little later on."* Switching to our carrier is normal DAYS before the rate
           exists (production finishes, the supplier sends weights and dims, then the broker
           quotes), so the card has to stay open across that gap or the reminder is invisible.

           ⛔ DELIBERATELY NOT `freightSettled`, which is the test po-lens.js uses. That one
           counts an our-carrier order as settled ON PURPOSE, because it also GATES PO
           GENERATION — and sending the PO before the freight number exists is exactly the
           sequence Ryan asked for. Tightening it there would block the PO. Two questions that
           look alike: "may we order?" and "is there anything left to do?"

           ⚠️ It re-collapses on its own the moment the cost lands, like the freight-charged
           reminder above; admin's own click still wins through collapsedSections. */
        var _fov = q.freightOverrides || {};
        var _fovFor = function (v) {
          if (_fov[v] != null) return _fov[v];
          var vl = String(v || '').trim().toLowerCase();
          var k = Object.keys(_fov).find(function (kk) { return String(kk).trim().toLowerCase() === vl; });
          return k ? _fov[k] : null;
        };
        var _owedOurFreight = vendors.some(function (v) {
          var o = _fovFor(v);
          return !!(o && o.mode === 'our-carrier' && o.cost == null);
        });
        if (_owedOurFreight) return false;
        /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (Ryan 2026-09-26 - RW-1317-C) — ⛔ A DIRECT PURCHASE IS PURCHASED.
           PowerBlock was bought with "Mark Purchased — no PO" (`ordering[v].purchasedDirectly`), so it
           has no PO and this rule held the card open at "PURCHASING (3/3)" forever. The header's count
           (isVendorAllBucketsPurchased) has honoured the flag since 2026-05-27; this is the same test,
           under any ordering key that names the supplier. */
        var _ordC = q.ordering || {};
        var _boughtDirect = function (v) {
          for (var _ck in _ordC) {
            if (Object.prototype.hasOwnProperty.call(_ordC, _ck) && (_ck === v || vendorNamesMatch(v, _ck))
              && _ordC[_ck] && _ordC[_ck].purchasedDirectly === true) return true;
          }
          return false;
        };
        return vendors.every(function(v) {
          if (_boughtDirect(v)) return true;
          return pos.some(function(p) {
            return p && p.status !== 'voided' && (p.status === 'sent' || !!p.downloadedAt) && vendorNamesMatch(p.vendor, v);
          });
        });
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19, v2) — Tracking card
         auto-collapses only when EVERY bucket/vendor is fulfilled, not
         just any. SHOP-1681 has broker + parcel buckets; collapsing the
         card after only one had tracking hid the still-incomplete row.
         Bucket-mode: every bucket has bucket.shipping.fulfillmentId.
         Non-bucket online: every vendor has shopifyFulfillmentId.
         Offline: every vendor has ord.tracking or ord.ordered.
         NOTE: the freight-balance card's "Fulfillment" rename has its
         own block below — we deliberately do NOT match
         'fulfillment' here (two separate cards can share the slug). */
      if (sectionId === 'shipping' || sectionId === 'tracking') {
        if (q.source === 'online') {
          var _hasBuckets = Array.isArray(q.shippingBuckets) && q.shippingBuckets.length > 0;
          if (_hasBuckets) {
            return q.shippingBuckets.every(function (b) {
              return !!(b && b.shipping && b.shipping.fulfillmentId);
            });
          }
          var _onlineOrd = q.ordering || {};
          var _onlineVendors = (q.financials || {}).vendors || [];
          if (_onlineVendors.length === 0) return !!q.onlineHasTracking;
          return _onlineVendors.every(function (v) { return _onlineOrd[v] && !!_onlineOrd[v].shopifyFulfillmentId; });
        }
        var ord = q.ordering || {};
        var shVendors = (q.financials || {}).vendors || [];
        if (shVendors.length === 0) return false;
        return shVendors.every(function(v) { return ord[v] && (ord[v].tracking || ord[v].ordered); });
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      if (sectionId === 'installation') {
        // Done = a date is scheduled (terminal state of this card; there's no
        // "mark complete" control) OR status==='complete'. Collapse when done.
        return !!(q.install && (q.install.scheduledDate || q.install.status === 'complete'));
      }
      /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-29) — the Freight Quote / Installation Quote
         cards. Ryan: they must NOT disappear once freight and install are added — "they collapse by
         default but stay visible, so admins can see costs, carrier name, etc. at a glance."

         So the rule is APPLIED, not merely filled in: a deal can carry every supplier's number and
         still be waiting for somebody to put it on the customer's quote, and that is precisely the
         state the card must stay OPEN for. The at-a-glance line lives in the heading, which is the
         one thing the collapsed rule keeps.

         ⛔ DISTINCT SLUGS FROM `installation` ABOVE, on purpose. That one belongs to the post-sale
         SCHEDULING card; two cards sharing a slug share collapse state and let one branch decide
         for both. */
      /* ⛔ COLLAPSED BY DEFAULT, OPEN ONLY WHILE SOMEBODY IS WAITING ON A NUMBER (Ryan 2026-08-29):
         *"make sure these cards are auto-collapsed unless freight has been requested and we're
         waiting for admin to come enter it, in which case then it can be auto-expanded."*

         So these two are the INVERSE of every other branch in this function. The rest answer "is
         this card's work finished?"; these answer "is this card the next thing that has to happen?"
         — which is the same rule the Adjust Order card got in PM19, and for the same reason: a card
         that is the outstanding action should not be one click away from being seen.

         ⚠️ TWO SIGNALS FOR "REQUESTED", AND BOTH COUNT. `wsFreight.status === 'requested'` is the
         rep asking admin through the Workstation's Request Quote dropdown; `freightQuotes[v]
         .requestedAt` is admin having emailed the supplier. Either way a cost is owed and nobody
         has typed it, which is exactly the state the card exists to serve. A skipped ask is not
         outstanding — the decision was recorded, so it collapses with everything else. */
      if (sectionId === 'freight-quote') {
        var _wf = q.wsFreight;
        if (!_wf) return true;
        var _fqAll = q.freightQuotes || {};
        var _askedSupplier = Object.keys(_fqAll).some(function (v) {
          var e = _fqAll[v] || {};
          return !!e.requestedAt && !e.skippedAt && !(e.cost != null && e.receivedAt);
        });
        var _askedAdmin = _wf.status === 'requested' || _wf.status === 'pending';
        if (!_askedSupplier && !_askedAdmin) return true;
        /* Answered already? Then nothing is outstanding and it folds away. */
        var _standalone = (_wf.vendors || []).filter(function (v) { return v && !v.linkedTo; });
        var _allSet = _standalone.length > 0 && _standalone.every(function (v) { return v.isSet; });
        return _allSet;
      }
      if (sectionId === 'installation-quote') {
        var _wi = q.wsInstallation;
        if (!_wi) return true;
        if (_wi.status !== 'requested' && _wi.status !== 'pending') return true;
        var _cos = _wi.companies || [];
        /* An installer with a cost and an estimate/official mark is an answered ask. */
        return _cos.length > 0 && _cos.every(function (c) { return c && c.type && c.type !== 'unset'; });
      }
      if (sectionId === 'commission') {
        var hasRep = q.repEarnsCommission !== false && parseFloat(q.commissionAmount || 0) > 0;
        var hasCfa = !!q.cfaCode && parseFloat(q.cfaCommissionAmount || 0) > 0;
        var hasReferral = !!q.referralName && parseFloat(q.referralAmount || 0) > 0;
        if (!hasRep && !hasCfa && !hasReferral) return false;
        return (!hasRep || !!q.commissionPaid) && (!hasCfa || !!q.cfaCommissionPaid) && (!hasReferral || !!q.referralPaid);
      }
      if (sectionId === 'freight-balance' || sectionId === 'fulfillment') {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Slug collision
           fix. The OFFLINE Tracking card renames its title to
           "Fulfillment" when the dims gate is active (offline + sold,
           see ~L9332 in main.js). That slug collides with the freight-
           balance card's "Fulfillment" slug, and the legacy isFreight-
           Balanced() gate below returns true vacuously for offline
           orders (no shipping buckets → "balanced by default") — auto-
           collapsing the card even when vendor tracking + dims are
           still pending. For offline-sold rows, fall through to the
           tracking-card gate (same predicate the offline Tracking card
           uses when its slug isn't renamed). The actual freight-balance
           card only renders on online orders with Flow A / buckets, so
           this gate is safe to scope to online. */
        if (q.source !== 'online') {
          var _offOrd = q.ordering || {};
          var _offVendors = (q.financials || {}).vendors || [];
          var _vendorShipDone = _offVendors.length === 0
            ? true
            : _offVendors.every(function(v) { return _offOrd[v] && (_offOrd[v].tracking || _offOrd[v].ordered); });
          var _fbDims = (q.freightBalance && q.freightBalance.dims) || null;
          var _fbDimsArr = _fbDims && typeof _fbDims === 'object'
            ? ((Array.isArray(_fbDims.groups) && _fbDims.groups.length > 0)
                ? _fbDims.groups
                : (Array.isArray(_fbDims.pallets) ? _fbDims.pallets : null))
            : null;
          var _dimsLogged = Array.isArray(_fbDimsArr) && _fbDimsArr.length > 0;
          return _vendorShipDone && _dimsLogged;
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        return isFreightBalanced(q) || !!(q.balanceCollected && q.balanceCollected.paid);
      }
      if (sectionId === 'linked-balance-reconciliation') {
        // Once reconciliation is balanced, the card has done its job —
        // admin doesn't need the running ledger open every time they
        // reopen the row. Stays expandable on click for verification.
        return !!(q.balanceReconciliation && q.balanceReconciliation.balanced === true);
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — BOTH KEYS, because the quote-level card's
         TITLE now varies with the deal. It reads "Samples" when it owns the list, Adjust and
         Request, and stays "Sample Tracking" on a sample-request deal where the lead card owns
         those and only tracking is left here. The collapse key is slugged from whichever title
         rendered, so a branch matching one of them would silently stop applying to half the
         deals — not a crash, which is exactly why it would go unnoticed: those cards would just
         render expanded on a screen full of ECORE orders. */
      if (sectionId === 'samples' || sectionId === 'sample-tracking') {
        // Default collapsed when there is nothing on the card yet — most ECORE
        // orders won't have a sample shipment attached, so it stays out of the
        // way until admin needs it. Once tracking is saved the card auto-expands
        // on next render so the rep can see the carrier + number without an
        // extra click.
        /* ⛔ A LIST NOW COUNTS AS SOMETHING, NOT JUST TRACKING. An admin who has built a sample
           list has done work on this card and a request is the next step; collapsing it because
           no tracking has come back yet would hide the deal's state at the one point in the chain
           where somebody is waiting on ECORE. Tracking-only was the right test when tracking was
           the only thing here. */
        /* ⚠️ THE SAME SOURCE THE CARD DRAWS FROM, not just `quoteSamples`. The picker writes
           lead-first, so an rfq-sourced deal keeps its list on the lead and the card reads
           `leadSamples` — asking only `quoteSamples` here would collapse exactly the deals that
           have a list, which is the opposite of the intent and would look like the rule working. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE COMMENT ABOVE DESCRIBED THIS BUG
           AND THE LINE UNDER IT HAD IT. `q.isLead ? leadSamples : quoteSamples` is false on exactly
           the deals the comment names ("an rfq-sourced deal keeps its list on the lead"), so it
           collapsed the deals that HAVE a list — the outcome the note says it is preventing. Same
           wrong predicate as the card itself; both ask the shared resolver now. */
        var _sampleList = (window.RQD && typeof RQD.samplesListFor === 'function')
          ? RQD.samplesListFor(q)
          : (Array.isArray(q.leadSamples) && q.leadSamples.length ? q.leadSamples : q.quoteSamples);
        var _hasSampleList = Array.isArray(_sampleList) && _sampleList.length > 0;
        var _hasSampleTracking = !!(q.quoteSampleTracking && q.quoteSampleTracking.number);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE CARD NOW DEFAULTS COLLAPSED.
           Ryan: *"Please auto-collapse samples card on deals that have the free-standing one (like
           RW-1309-A)."* The ✓ the header gained in the same change is what replaces it at a glance:
           a rep can see samples were involved, and expands only if they want the detail.

           ⚠️ ONE EXCEPTION KEPT, AND IT IS RYAN'S OWN EARLIER RULE. A list with no tracking yet is
           the state where somebody is waiting on ECORE, and the note this branch already carried
           says collapsing it "would hide the deal's state at the one point in the chain where
           somebody is waiting". That case is not RW-1309-A (which has tracking AND a delivery), so
           keeping it satisfies both instructions rather than trading one for the other. Say the
           word and it becomes an unconditional `return true`. */
        if (_hasSampleList && !_hasSampleTracking) return false;
        return true;
      }
      return false;
    }

    // === applyCollapseState — extracted from main.js:6772-6810 ===
    // Tag every workflow card under the detail panel as collapsible, slug
    // its section, and apply the saved collapsed state. Run after every
    // detail HTML render so state persists across re-renders.
    function applyCollapseState(detailEl, q) {
      if (!detailEl || !q) return;
      var ref = q.reference;
      var cards = detailEl.querySelectorAll('.rqd-detail__arap');
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-31) — ⛔ RE-EVALUATED ON EVERY RENDER, NOT
         ONCE PER REF PER SESSION.

         `sectionsAutoCollapsedRef[ref]` latched on the FIRST render of a deal, and a card that
         becomes complete later in the session then never collapsed. That is not a rare edge:
         the first render routinely happens against data that is a step behind — the board paints
         from a 6h sessionStorage cache and revalidates after — so the deal Ryan is actively
         working is exactly the one whose latch was set while its cards were still incomplete.
         On SHOP-2003 Purchasing and Freight Balance both earned their header ✓ and both stayed
         open. Ryan: *"Shouldn't they be closed given that they both have checkmarks next to the
         heading in the header?"* Yes.

         ⛔ THE REP'S OWN TOGGLE STILL WINS, AND THAT IS WHY THE LATCH EXISTED. It is now recorded
         explicitly (`manuallyToggledSections`) instead of being inferred from "we have rendered
         this ref before" — absence of a `collapsedSections` entry means "expanded", which is
         also the state of a card nobody has touched, so the old store could not tell the two
         apart. Once the rep opens a completed card it stays open for the session.

         Only ever COLLAPSES. A card that stops being complete (a voided PO, say) is deliberately
         not re-expanded — auto-opening something the rep had closed is the more annoying of the
         two failures. `sectionsAutoCollapsedRef` is left in place: it is a shared object other
         modules are wired to, and nothing here needs to break that. */
      if (!collapsedSections[ref]) collapsedSections[ref] = {};
      sectionsAutoCollapsedRef[ref] = true;
      var _manual = manuallyToggledSections[ref] || {};
      cards.forEach(function(card) {
        var sectionId = sectionIdFor(card);
        if (!sectionId || _manual[sectionId]) return;
        /* CUSTOM_SUPPLIERPAYDIGEST_BY_CLAUDE (Ryan 2026-09-13) — ⛔ A CARD THAT DREW AN UNPAID
           SUPPLIER STAYS OPEN, whichever card that is. It outranks the fully-fulfilled sweep on
           purpose: the goods arriving is exactly when the money going out is the only step left,
           so collapsing then hides the last open thing on the deal.
           ⚠️ THE CARD SAYS SO ITSELF (`data-has-payables`) rather than this re-deciding which card
           drew what. That question is settled once, in buildDetailHtml, and a second copy of it
           here could only ever disagree - which would show as a card collapsing over money it is
           the only place to see. It is admin-only for free: a rep's cards never carry the mark. */
        if (card.dataset && card.dataset.hasPayables) return;
        if (shouldAutoCollapse(sectionId, q)) collapsedSections[ref][sectionId] = true;
      });
      cards.forEach(function(card) {
        var titleEl = card.querySelector('.rqd-detail__section-title');
        if (!titleEl) return;
        var sectionId = sectionIdFor(card);
        card.classList.add('rqd-detail__arap--collapsible');
        if (sectionId) card.dataset.section = sectionId;
        var isCollapsed = !!(collapsedSections[ref] && collapsedSections[ref][sectionId]);
        card.classList.toggle('rqd-detail__arap--collapsed', isCollapsed);
        // Inject (or refresh) the +/− chevron at the right edge of the title.
        var chevron = titleEl.querySelector('.rqd-arap__chevron');
        if (!chevron) {
          chevron = document.createElement('span');
          chevron.className = 'rqd-arap__chevron';
          titleEl.appendChild(chevron);
        }
        chevron.textContent = isCollapsed ? '+' : '−';
      });
    }

    // === renderOrderContentsCard — extracted from main.js:6822-7135 ===
    // Standalone Order Contents card — same shape used by online (eager) and
    // offline (lazy) paths. Shared so the two paths render byte-identical.
    // `fees` is an optional financials object: { freightCharged, installCharged,
    // cardFee, taxes, grandTotal }. `opts.isOnline` differentiates the two
    // paths: offline always shows Shipping & Freight + Installation rows
    // (even when not yet entered, with "—" placeholders) since the rep
    // needs to know they still need to be filled in. Online suppresses
    // Installation entirely (Shopify doesn't sell installs) and only shows
    // Shipping & Freight when > 0. Hr-style dividers separate the three
    // bands (items / fees / total).
    /* CUSTOM_ROLLMATH_BY_CLAUDE (ONYX c5ef9e57c97f + 4d660f6996ea, Ryan 2026-09-30, SHOP-2112) — the
       arithmetic under a saved cut list: "Rolls 805 sq ft · Paid 650 sq ft · +155 sq ft (+$1,542.25)",
       plus "Adjust to 805" for an admin on a sold online order (Ryan's pick from a rendered before/after).
       Every number is the WORKER's (q.rollLengths.lines, lib/roll-lengths.js) — nothing is re-derived
       here, so the row and what an agent reads cannot disagree. A line sold per roll (unitMismatch) or
       with no saved lengths draws nothing. */
    function rollMathLine(it, opts, ref) {
      var rl = opts && opts.rollLengths;
      var lines = (rl && Array.isArray(rl.lines)) ? rl.lines : [];
      var L = null;
      for (var i = 0; i < lines.length; i++) { if (lines[i] && it && lines[i].lineItemId && lines[i].lineItemId === it.lineItemId) { L = lines[i]; break; } }
      if (!L || !L.totalSqFt || L.unitMismatch || L.deltaQty == null) return '';
      var n = function (v) { return Number(v).toLocaleString('en-US'); };
      var money = function (v) { return '$' + Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
      var diff;
      if (L.deltaQty === 0) diff = '<span class="rqd-roll-math__ok">matches</span>';
      else {
        var sign = L.deltaQty > 0 ? '+' : '&minus;';
        diff = '<span class="rqd-roll-math__diff">' + sign + n(Math.abs(L.deltaQty)) + ' sq ft'
          + (L.deltaAmount != null ? ' (' + sign + money(L.deltaAmount) + ')' : '') + '</span>';
      }
      var btn = (L.deltaQty !== 0 && opts && opts.rollAdjustable && ref)
        ? ' <button type="button" class="rqd-roll-adjust-btn" data-ref="' + esc(ref) + '" data-line="' + esc(L.lineItemId) + '" data-to="' + esc(String(L.totalSqFt)) + '"'
          + ' title="Open Adjust Order with this line set to the saved roll total. You review and commit.">Adjust to ' + n(L.totalSqFt) + '</button>'
        : '';
      return '<span class="rqd-roll-math">Rolls ' + n(L.totalSqFt) + ' sq ft &middot; Paid ' + n(L.paidQty) + ' sq ft &middot; ' + diff + btn + '</span>';
    }

    /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - the alternate source this line's PO goes to,
       from the line itself (online rows carry it) or the deal's per-line switches (offline). */
    function _altVendorOf(it, opts) {
      if (!it) return '';
      if (it.altSource && it.vendor) return it.vendor;
      var m = opts && opts.lineVendorOverrides;
      var e = m && it.lineItemId ? m[it.lineItemId] : null;
      return (e && e.vendor) || '';
    }
    function renderOrderContentsCard(items, fees, opts) {
      var isOnline = !!(opts && opts.isOnline);
      // Admin gets a Cost column with inline-editable per-line cost. Online
      // orders only — offline rfq line items already carry their cost
      // through workstation_meta.items and are editable from the Workstation.
      var showCostCol = !!(opts && opts.isAdmin) && isOnline;
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-14) — the Qty pencil is the ONE of the
         three edits an offline deal also needs, so it gets its own gate.

         Cost and SKU stay online-only for the stated reason: an offline line carries them
         through workstation_meta and they're editable from the Workstation. Qty is the
         exception — once a deal is SOLD the Workstation can no longer change it (the draft
         is completed; Shopify locks it), and the vendor PO reads that draft, so there was
         no way to tell a supplier to ship 1,408 sq ft instead of 1,508. RW-1297-E.

         The override feeds effectiveQty in po-routes.js — the PO and COGS, never customer
         billing — and that endpoint was never online-gated; only this pencil was. */
      var showQtyEdit = !!(opts && opts.isAdmin)
        && (isOnline || (opts && opts.lifecycleStatus === 'sold'));
      var ref = opts && opts.ref ? opts.ref : '';
      var subtotal = 0;
      var rows = '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — append auto-reconciled GOODS (balanceGood custom
         costs) as synthetic line items so they render through the SAME row template
         (title · qty · price · line total) + flow into the subtotal, instead of being
         invisible here. Their COST folds into COGS on the Financials card (handled
         there); here we show the customer-facing price like any other product line. */
      var _allItems = items.slice();
      (opts && Array.isArray(opts.customCosts) ? opts.customCosts : []).forEach(function (cc) {
        if (!cc || !cc.balanceGood) return;
        var _q = parseInt(cc.goodQty, 10) || 1;
        var _up = parseFloat(cc.goodUnitPrice) || 0;
        _allItems.push({
          title: (cc.goodTitle || cc.label || 'Item') + (cc.sourceBalanceRef ? ' (from ' + cc.sourceBalanceRef + ')' : ''),
          quantity: _q,
          unitPrice: _up,
          lineTotal: Math.round((_up * _q) * 100) / 100,
          unitCost: parseFloat(cc.goodUnitCost) || 0,
          sku: cc.goodSku || '',
        });
      });
      _allItems.forEach(function(it) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Effective line total. When admin has
           overridden the qty (✎ pencil → wm.lineQtyOverrides), Shopify's
           original it.lineTotal still reflects the pre-override qty × unit
           price, which left the displayed Total column stale after a qty
           change. Use effectiveQty × unitPrice when overridden so the row
           total + subtotal both move in lockstep with the admin's edit. */
        var _effQty = (it.effectiveQuantity != null) ? it.effectiveQuantity : (it.quantity || 0);
        var _qtyOver = !!it.quantityOverridden;
        var effectiveLineTotal = _qtyOver
          ? _effQty * (parseFloat(it.unitPrice) || 0)
          : (parseFloat(it.lineTotal) || 0);
        subtotal += effectiveLineTotal;
        var titleHtml = esc(it.title || '');
        /* CUSTOM_PILLWRAP_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - the pills after an item's name (freight-quoted, no
           weight, pending payment, -> alternate source) are collected here and drawn as ONE group that wraps as a unit,
           so two pills never split with the first left alone at the end of the name's line. */
        var _tailTags = [];
        /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-16) — THE COLOUR COMES OUT OF THE NAME.
           Ryan: "I like the color being there, but I think it should be its own sub-text row on
           all flooring rows". It has a row of its own now (below), so appending it here as well
           would print it twice — and on a swap it was the whole reason two rows for the same
           goods read as two different products.
           ⛔ ONLY WHEN THE COLOUR ROW REALLY SAYS THE SAME THING. A Legend machine's variant
           title is not a colour and gets no colour row, so its inline suffix must stay; the test
           is equality with the resolved colour, never "this line has a variant". */
        var _colorLine = String(it.color || '').trim();
        var _variantInline = !!(it.variantTitle && _colorLine !== String(it.variantTitle).trim());
        /* CUSTOM_LINETITLE_BY_CLAUDE (Ryan 2026-10-09) — ONE admin pencil for the whole name: the item
           name AND the variant shown after it ("Name — Variant"). Same gate as SKU / Qty / Cost (admin +
           online). The edit box opens on "Name - Variant" (a plain hyphen: the PO prints it as typed) and
           the saved text replaces BOTH, so an override hides the separate variant suffix here and the
           "Variant:" row on the PO. Card + vendor PO only; the Shopify order and customer emails keep
           Shopify's title. The + flags button and the pills are added around it later and stay outside
           the click target. The colour row is untouched (its own sub-row). */
        if (showCostCol && it.lineItemId) {
          var _titleOver = !!it.titleOverridden;
          var _origFull = (it.title || '') + (_variantInline ? ' - ' + it.variantTitle : '');
          /* An override saved over the name ALONE (before the pencil carried the variant, 2026-10-09
             round 1) does not stand for the variant: it keeps the variant suffix, and the edit box
             re-opens on "Override - Variant". Only an override flagged titleIncludesVariant hides it. */
          var _keepVariant = _variantInline && !(_titleOver && it.titleIncludesVariant);
          var _nameShown = _titleOver ? (it.effectiveTitle || '') : (it.title || '');
          var _editVal = _nameShown + (_keepVariant ? ' - ' + it.variantTitle : '');
          var _shownHtml = esc(_nameShown)
            + (_keepVariant ? ' <span class="rqd-online-items__variant">— ' + esc(it.variantTitle) + '</span>' : '');
          titleHtml = '<span class="rqd-line-title-edit' + (_titleOver ? ' rqd-line-title-edit--overridden' : '') + '"'
            + ' data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId) + '"'
            + ' data-original-title="' + esc(_origFull) + '"'
            + ' data-edit-value="' + esc(_editVal) + '"'
            + ' data-has-variant="' + (_variantInline ? '1' : '0') + '"'
            + ' title="' + (_titleOver
                ? 'Name override active (Shopify: ' + esc(_origFull) + '). Click to edit; clear the field to revert. Used on the vendor PO, not the Shopify order.'
                : 'Click to correct the item name and variant for Order Contents and the vendor PO (does not change the Shopify order)') + '">'
            + '<span class="rqd-line-title-value">' + _shownHtml + '</span> <span class="rqd-fin-edit-icon">&#9998;</span>'
            + '</span>';
        } else if (_variantInline) {
          titleHtml += ' <span class="rqd-online-items__variant">— ' + esc(it.variantTitle) + '</span>';
        }
        /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — a line Adjust Order put on the PAID
           order after the quote's draft froze. It is NOT on the quote, so it has to say so: a $0.00
           row sitting silently among priced ones reads as a pricing bug, and a priced one that
           appeared without the quote total moving reads as a worse one. The tooltip carries who and
           when, which is the question a person actually has when they notice the row. */
        if (it._addedAfterPayment) {
          titleHtml += ' <span class="rqd-oc-added'
            + '" title="' + esc('Added to the order after it was paid'
              + (it._addedBy ? ' by ' + it._addedBy : '')
              + (it._addedAt ? ' on ' + String(it._addedAt).slice(0, 10) : '')
              + (it._noCharge ? ', at no charge' : '')) + '">'
            + (it._noCharge ? 'FREE &middot; added after payment' : 'added after payment')
            + '</span>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Freight-quoted check ORs the Shopify
           product tag (catalog items) with the admin per-line flag
           (it.isFreightQuotedFlag — set via /line-freight-quoted-flag for
           custom workstation items / substituted child lines that don't
           carry the Shopify product tag). Either path lights up the green
           pill AND moves the line into the broker bucket on the worker
           side (mirrored gate in quote-dashboard.js bucket derivation). */
        /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-28) — a third source: the shipment-weight
           rule. `q._autoFreightByVendor` is computed on the worker (quote-dashboard.js) and keyed
           by canonical vendor; a line inherits its vendor's verdict, which is the same
           same-vendor inheritance the bucket derivation applies.

           ⛔ AN EXPLICIT OFF WINS. Clearing the toggle now stores `off: true` rather than deleting
           the key, precisely so a human can overrule a derived rule; honouring it here is what
           makes the + button work in both directions. */
        var _afv = (opts && opts.autoFreightByVendor) || {};
        var _afMine = _afv[String(it.vendor || '').trim()] || _afv[String(it.vendor || '').trim().toLowerCase()] || null;
        var _autoFq = !it.freightQuotedFlagOff && !!(_afMine && _afMine.tripped);
        var isFreightQuoted = !it.freightQuotedFlagOff
          && ((it.productTags || []).some(function(t) { return String(t).toLowerCase() === 'freight-quoted'; })
            || !!it.isFreightQuotedFlag
            || _autoFq);
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        if (isFreightQuoted) {
          /* ⛔ SAY WHERE IT CAME FROM when nobody clicked it. This pill promotes the deal to Flow A,
             the flow that bills the customer a separate freight balance — an automatic money-shaped
             state change that must never look like it was always there. */
          var _fqLabel = 'freight-quoted';
          var _fqTitle = '';
          if (_autoFq && !it.isFreightQuotedFlag) {
            _fqLabel += ' · auto ' + Math.round(_afMine.lb) + ' lb';
            _fqTitle = ' title="Auto: the shipment from this vendor is ' + Math.round(_afMine.lb)
              + ' lb, at or over the ' + _afMine.threshold + ' lb pallet threshold. Use + to override."';
          }
          _tailTags.push('<span class="rqd-online-items__tag rqd-online-items__tag--green"' + _fqTitle + '>' + _fqLabel + '</span>'); // CUSTOM_PILLWRAP_BY_CLAUDE
        }
        /* ⛔ ITS OWN `if`, NOT AN `else if` ON THE FREIGHT PILL — and that mattered more than it
           looks. As an else-branch the chip could only ever render on a line that is NOT
           freight-quoted, so on SHOP-1997 it appeared on the weld rod and the adhesive (which are
           fine) and was STRUCTURALLY UNABLE to appear on the laminate roll, which is the only line
           actually missing a weight. Scoping it to the offending row without this would have moved
           the chip from the wrong rows to no rows at all.

           A line's shipping flow and whether Shopify knows its weight are independent facts, and a
           freight-quoted line with no weight is exactly the combination worth shouting about. */
        if (_afMine && !_afMine.known && isOnline && it.weightKnown === false
            && !(opts && opts.orderIsFreightQuoted)) {
          /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-29) — ⛔ ON THE LINE THAT IS ACTUALLY
             MISSING A WEIGHT, not on every line of the vendor.

             The chip makes a VENDOR-level statement ("this shipment could not be totalled") but
             renders per LINE, so it appeared on rows that carry a perfectly good weight. Ryan, on
             SHOP-1997: *"Not sure why it says WEIGHT UNKNOWN though, I'm pretty sure the weights
             for both have been added to Shopify. I know the TRD-000002 has, b/c I added it today
             - 60lbs."* He was right — the weld rod is 3 lb and the adhesive 60 lb. The line with
             no usable weight was the LAMINATE ROLL (`VYLM-000092`, set to 0.0 lb in Shopify), and
             the chip on his two new items was talking about a third row.

             ⚠️ The explanation was only ever in the `title`, which is the one place Ryan has said
             it cannot live — nobody hovers, and an agent reading the DOM sees a bare chip. Putting
             it on the offending row makes the placement itself the explanation.

             ⚠️ `weightKnown === false` EXPLICITLY, not falsy: the field is absent on offline /
             Workstation lines, and `!it.weightKnown` would badge every one of them — which is the
             wallpaper the `isOnline` guard already exists to prevent. */
          /* ⛔ UNKNOWN IS NOT UNDER-THRESHOLD. Some products carry no weight in Shopify (measured
             2026-08-28: 44 of 364 untagged, 40 of them Spirit Fitness cardio). Rendering nothing
             would read as "assessed, came in light" for a shipment that was never assessable.

             ⚠️ ONLINE ORDERS ONLY. An offline / Workstation line has no Shopify variant behind it
             and so never has a weight — badging every one of them would turn a real data gap into
             wallpaper, and the chip would stop meaning anything on the orders where it matters. */
          /* ⛔ NARROWED TO WHERE IT IS THE ONLY WARNING THERE IS (Ryan 2026-08-29). It used to
             render whenever a weight was missing; it now renders only when the pallet decision has
             NOT already been made some other way. On a freight-quoted order the tag has already
             sent the shipment to the broker and the weight rule's verdict changes nothing — which
             is what made it pure noise on SHOP-1997.

             What survives is the case it was built for and nothing else covers: the 44 active
             products with no weight are ALL untagged, 40 of them Spirit Fitness cardio. A 400 lb
             treadmill with no tag and no weight is caught by the tag (there is none), by the weight
             rule (it cannot total the vendor), and by nothing else at all. It ships parcel unless
             a human notices.

             ⚠️ Its own class, because the shared `__tag` base is a PALE RED that is barely legible
             on the SOLD card's light green — Ryan: *"the peach/orange color on the light green is
             hard to see"* — and pale red now belongs to money owed. */
          _tailTags.push('<span class="rqd-online-items__tag rqd-online-items__tag--noweight"' // CUSTOM_PILLWRAP_BY_CLAUDE
            + ' title="No weight on this item in Shopify, so the pallet rule could not total this'
            + ' vendor&#39;s shipment and nothing has assessed whether it needs freight. Check by hand."'
            + '>no weight in Shopify</span>');
        }
        /* ⛔ OUTSIDE THE FREIGHT if/else ABOVE, and it has to be. First cut of this landed
           INSIDE the `if (isFreightQuoted)` branch, so the chip could only ever render on a
           freight-quoted line — and the two items Ryan had just added carry no freight tag,
           so it rendered for nobody while the data behind it was perfectly correct. Whether a
           customer has paid for a line has nothing to do with how it ships. */
        /* CUSTOM_PENDINGPAY_BY_CLAUDE (Ryan 2026-08-29) — goods on the order that the customer has
           not paid for yet. Ryan, after adding an adhesive and a weld rod to the already-paid
           SHOP-1997: *"I'm wondering if they should appear on the ORDER CONTENTS table with a
           'PENDING PAYMENT' pill/tag next to them perhaps?"* They already appeared — reading
           exactly like the line that IS paid for.

           ⚠️ Set by the worker from the ADJUSTMENT LEDGER via the same `collectUnion` the collect
           email itemises from, so this chip and the customer's invoice cannot disagree about which
           goods are outstanding.

           ⚠️ A quantity INCREASE on an already-paid line says how many are unpaid: a bare "pending
           payment" on a row the customer has largely paid for would be its own small lie. */
        if (it.pendingPaymentQty > 0) {
          _tailTags.push('<span class="rqd-online-items__tag rqd-online-items__tag--pending"' // CUSTOM_PILLWRAP_BY_CLAUDE
            + ' title="' + (it.pendingPaymentPartial
                ? it.pendingPaymentQty + ' of these are on an outstanding balance the customer has not paid yet.'
                : 'This item is on an outstanding balance the customer has not paid yet.')
            + ' Collect it from the Adjust Order card.">'
            + (it.pendingPaymentPartial ? (it.pendingPaymentQty + ' pending payment') : 'pending payment')
            + '</span>');
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Per-line flags affordance. Generic
           `+` button (admin only, requires lineItemId) that opens a small
           popover with two toggles: NEW (vendor PO highlight) and Freight
           Quoted (broker-bucket flag). Each toggle persists via its own
           worker endpoint (/line-new-flag and /line-freight-quoted-flag).
           When NEW is set, a separate yellow NEW pill renders next to the
           title (was previously the button label "NEW ✓"). Freight Quoted
           reuses the existing green `freight-quoted` pill above. */
        var rowNewClass = '';
        var _isAdminOpt = !!(opts && opts.isAdmin);
        if (_isAdminOpt && it.lineItemId) {
          var isNewItem = !!it.isNew;
          /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-28) — ⛔ THE CHECKBOX SHOWS THE EFFECTIVE
             STATE, NOT JUST THE MANUAL FLAG. Ryan, on an auto-flagged deal: *"my instinct would be
             to click the '+' button next to the relevant line item(s) and uncheck the 'freight
             quoted' box, but it's already unchecked despite the auto tag being added."*

             It read `isFreightQuotedFlag` alone, so a line that is freight-quoted by the WEIGHT
             RULE — or by the Shopify product tag, which had the same problem quietly since long
             before today — showed an unchecked box beside a lit pill. Clicking it would have
             turned the manual flag ON, the opposite of the intent; the only route to an override
             was to check it and then uncheck it.

             `isFreightQuoted` is the same value the pill above renders, so the control and the
             badge can no longer disagree. Unchecking writes the explicit off-marker, which beats
             all three sources. */
          var isFqFlag = isFreightQuoted;
          var pillsHtml = '';
          if (isNewItem) {
            pillsHtml = '<span class="rqd-online-items__tag rqd-online-items__tag--new" title="Marked NEW for the vendor PO — open the + menu to clear">NEW &#10003;</span> ';
          }
          var flagsBtn = '<span class="rqd-online-items__flags-anchor">'
            + '<button type="button" class="rqd-online-items__flags-btn" data-line-flags-toggle="1"'
            + ' data-ref="' + esc(ref) + '"'
            + ' data-line-item-id="' + esc(it.lineItemId) + '"'
            + ' data-is-new="' + (isNewItem ? '1' : '0') + '"'
            + ' data-is-fq="' + (isFqFlag ? '1' : '0') + '"'
            /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - the vendor the customer bought and any
               alternate source the line's PO now goes to, for the third option in the menu. */
            + ' data-vendor="' + esc(it.brandVendor || it.vendor || '') + '"'
            + ' data-alt-vendor="' + esc(_altVendorOf(it, opts)) + '"'
            + ' title="Flag this line: NEW (vendor PO), Freight Quoted (broker bucket), or buy it from an alternate source">+</button>'
            + '</span>';
          titleHtml = flagsBtn + ' ' + pillsHtml + titleHtml;
          /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - at the END of the name, after the
             freight-quoted pill, in the same dark forest green. */
          if (_altVendorOf(it, opts)) {
            _tailTags.push('<span class="rqd-online-items__tag rqd-online-items__tag--alt" title="This line&#39;s PO goes to ' + esc(_altVendorOf(it, opts)) + ' instead of ' + esc(it.brandVendor || it.vendor || '') + '. Open the + menu to put it back.">&rarr; ' + esc(_altVendorOf(it, opts)) + '</span>'); // CUSTOM_PILLWRAP_BY_CLAUDE
          }
          if (isNewItem) rowNewClass = ' class="rqd-online-items__row--new"';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        // Effective unit cost wins over Shopify's variant cost when admin
        // has set a per-line override (see /line-cost-override). The
        // overridden state gets a pencil-in icon + edited tint so admin
        // can see which lines have been touched.
        var effectiveCost = (it.effectiveUnitCost != null) ? it.effectiveUnitCost : (it.unitCost || 0);
        var overridden = !!it.costOverridden;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Bundle parent
           lines render Cost as a READ-ONLY computed sum from the
           component sub-rows below. Editing happens at the component
           level via the sub-row's pencil. */
        var costFromBundleSum = !!it.costFromBundleSum;
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        var costCell = '';
        if (showCostCol) {
          if (costFromBundleSum) {
            costCell = '<td class="rqd-line-cost-edit rqd-line-cost-edit--bundle-sum" title="Computed sum of component costs below. Edit each component to change it.">' +
              '<span class="rqd-line-cost-value">' + money(effectiveCost) + '</span>' +
            '</td>';
          } else {
            var editIcon = '<span class="rqd-fin-edit-icon">&#9998;</span>';
            var overrideClass = overridden ? ' rqd-line-cost-edit--overridden' : '';
            costCell = '<td class="rqd-line-cost-edit' + overrideClass + '" data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId || '') + '" title="' + (overridden ? 'Override active — click to edit. Shopify variant cost: ' + money(it.unitCost || 0) : 'Click to override the Shopify variant cost') + '">' +
              '<span class="rqd-line-cost-value">' + money(effectiveCost) + '</span> ' + editIcon +
            '</td>';
          }
        }
        // Qty cell — admin + online orders get inline-edit, same pattern as
        // cost. Effective qty (override or Shopify-original) is what we
        // display + ship via PO. Customer-paid lineTotal stays from
        // Shopify; a qty correction reconciles via a linked balance order.
        var effectiveQty = (it.effectiveQuantity != null) ? it.effectiveQuantity : (it.quantity || 0);
        var qtyOverridden = !!it.quantityOverridden;
        var qtyCell;
        if (showQtyEdit && it.lineItemId) {
          var qtyEditIcon = '<span class="rqd-fin-edit-icon">&#9998;</span>';
          var qtyOverrideClass = qtyOverridden ? ' rqd-line-qty-edit--overridden' : '';
          /* Offline wording names the DRAFT, not "Shopify qty" — on an offline row the
             quantity the customer bought lives on the quote's draft order, and the tooltip
             has to make clear this changes what the VENDOR ships, not what was billed. */
          var qtyTitle = qtyOverridden
            ? 'Override active — click to edit. ' + (isOnline ? 'Customer originally paid for ' : 'Quote says ') + (it.quantity || 0) + '.'
            : (isOnline
              ? 'Click to override the Shopify qty (used for the PO + COGS, not customer billing)'
              : 'Click to override the quantity for the vendor PO + COGS. Does not change the quote, the order, or what the customer was billed.');
          qtyCell = '<td class="rqd-line-qty-edit' + qtyOverrideClass + '" data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId || '') + '" title="' + qtyTitle + '">' +
            '<span class="rqd-line-qty-value">' + effectiveQty + '</span> ' + qtyEditIcon +
          '</td>';
        } else {
          qtyCell = '<td>' + effectiveQty + '</td>';
        }
        // SKU cell — admin + online orders get inline-edit, same pattern as
        // cost/qty. effectiveSku (override wins over Shopify variant SKU) is
        // what we display + ship via PO. Clearing the field reverts to the
        // Shopify SKU. Non-admin / offline render the plain value.
        var effectiveSku = (it.effectiveSku != null) ? it.effectiveSku : (it.sku || '');
        var skuOverridden = !!it.skuOverridden;
        var skuCell;
        if (showCostCol) {  // showCostCol === isAdmin && isOnline — same gate
          var skuEditIcon = '<span class="rqd-fin-edit-icon">&#9998;</span>';
          var skuOverrideClass = skuOverridden ? ' rqd-line-sku-edit--overridden' : '';
          var skuTitle = skuOverridden
            ? 'SKU override active — click to edit (used for the vendor PO). Clear the field to revert to the Shopify SKU.'
            : 'Click to override the SKU for the vendor PO (does not change the Shopify order)';
          // CUSTOM_LEGENDSPEC_BY_CLAUDE — was style="color:#888", an unreadable gray on the SOLD card's
          // green panel (the --rqd-detail--sold scope redefines --rq-text* to greens). The token
          // inherits whichever theme the card is wearing; the literal never could.
          var skuValueInner = effectiveSku ? esc(effectiveSku) : '<span class="rqd-cell-empty">—</span>';
          skuCell = '<td class="rqd-line-sku-edit' + skuOverrideClass + '" data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId || '') + '" title="' + skuTitle + '">' +
            '<span class="rqd-line-sku-value">' + skuValueInner + '</span> ' + skuEditIcon +
          '</td>';
        } else {
          skuCell = '<td>' + esc(effectiveSku || '—') + '</td>';
        }
        if (_tailTags.length) titleHtml += ' <span class="rqd-online-items__tags">' + _tailTags.join(' ') + '</span>'; // CUSTOM_PILLWRAP_BY_CLAUDE
        rows += '<tr' + rowNewClass + '>' +
          '<td>' + titleHtml + '</td>' +
          skuCell +
          qtyCell +
          '<td>' + money(it.unitPrice || 0) + '</td>' +
          costCell +
          '<td>' + money(effectiveLineTotal) + '</td>' +
          '</tr>';
        /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-16) — THE COLOUR, ITS OWN ROW.
           Same shape as the Roll Layout row under it, because it answers the same kind of question
           about the line and Ryan asked for the same UI. Above the cut list on purpose: the colour
           says WHAT the material is, the cuts say how it arrives.
           ⚠ The worker decides what counts as a colour (lib/flooring-color.js, vendor-gated),
           so this draws a row whenever one is present and never asks "is this flooring". */
        if (_colorLine) {
          rows += '<tr class="rqd-online-items__roll-sub rqd-online-items__color-sub">' +
            '<td>' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              '<span class="rqd-color-line">&#127912; Color: ' +
                '<span class="rqd-color-line__value">' + esc(_colorLine) + '</span>' +
              '</span>' +
            '</td>' +
            '<td colspan="' + (showCostCol ? 5 : 4) + '"></td>' +
          '</tr>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — Roll layout sub-row for
           flooring rolls. it.rollLayout = the calculator's "Roll Layout" (online
           line-item property) or the rep's roll sub-text (offline). it.isRoll =
           the product carries the custom.roll_width metafield. If it's a roll
           with a layout, show it; if it's a roll with NO layout, flag it (amber)
           — the same gap the Generate-PO warning catches. */
        if (it.rollLayout) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — the cut list lives in the ITEM
             column, not spanning the table. A big flooring order (RW-1196-A: 9 groups on one
             line) used to wrap across the full card width, reading as a wall of text under
             the SKU/Qty/Price columns. Now it sits under the item it belongs to and wraps
             within a capped width (the desktop table is auto-layout, so the cap is what stops
             a long list from stretching the column and distorting the whole table).
             Each cut is its own nowrap chip so a wrap lands BETWEEN groups, never inside one
             ("(10x) 45' x" / "6'"). rollCuts is offline-only; online layouts (one calculator
             string) fall back to the joined rollLayout, unchanged. */
          var _rollCuts = Array.isArray(it.rollCuts) ? it.rollCuts : [];
          var _rollBody;
          if (_rollCuts.length) {
            _rollBody = (it.rollLabel ? '<span class="rqd-roll-layout__label">' + esc(it.rollLabel) + '</span>' : '') +
              _rollCuts.map(function (c) {
                return '<span class="rqd-roll-layout__cut">' + esc(c) + '</span>';
              }).join(' '); // the space IS the break opportunity — adjacent inline-blocks
                            // with no whitespace between them can't wrap.
          } else {
            // Free-text override / online calculator string — no group structure, so it stays
            // wrappable (a long nowrap string would overflow the column into a scrollbar).
            _rollBody = '<span class="rqd-roll-layout__cut rqd-roll-layout__cut--free">' + esc(it.rollLayout) + '</span>';
          }
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — (a) the cut list is now EDITABLE
             from here, and (b) it flags itself when more of the same material arrived after it was
             written.

             (a) reuses the Fulfillment card's existing roll-lengths calculator rather than building
             a second editor: its handler is delegated on the whole detail panel and keys only off
             `.rqd-roll-lengths-btn` + data-ref, so the button works from any card. Two editors for
             one field would be two things to keep in step.

             (b) is the half Ryan actually needs. Linking a balance child adds MORE of a product the
             parent already has (RW-1325-A put another 224 sq ft of LMNT-000507 onto SHOP-1933), and
             the cut list on screen still describes only the ORIGINAL quantity — it is silently
             short, and nothing said so. A layout that covers 280 of 504 sq ft is worse than a
             missing one, because it looks finished. Detected off the balanceGood custom costs
             (same signal the PO merge uses) matching THIS line's SKU. */
          var _rollMoreAdded = false;
          if (it.sku) {
            var _skuLc = String(it.sku).toLowerCase().trim();
            _rollMoreAdded = (opts && Array.isArray(opts.customCosts) ? opts.customCosts : [])
              .some(function (cc) {
                return cc && cc.balanceGood && cc.goodSku
                  && String(cc.goodSku).toLowerCase().trim() === _skuLc;
              });
          }
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17, v2) — CLICK THE LINE, NOT A BUTTON.
             v1 put a small "Edit" button beside the layout. Ryan asked for the interaction the red
             no-layout badge implies: a solid pill, and the whole row clickable with no separate
             control. Better here for a specific reason — the thing you want to act on IS the cut
             list, so making the cut list itself the target removes a hop, and a roll row with
             nothing wrong carries no stray button.

             The CELLS take `.rqd-roll-lengths-btn` + data-ref, so the EXISTING delegated handler
             (dashboard-main.js ~L2570, `e.target.closest('.rqd-roll-lengths-btn')`) opens the
             Fulfillment roll-lengths calculator with no new wiring. It is admin-gated there, so a
             rep's click is a no-op rather than an error. */
          var _rollClickable = !!(opts && opts.isAdmin && ref);
          /* ⛔ THE HOOK GOES ON THE CELLS, NEVER ON THE <tr>. dashboard-main.js:2552 runs
             `e.target.closest('tr[data-ref]')` and calls toggleDetail() on whatever it finds.
             Nothing inside a detail panel used to match that (the panel's own row carries
             data-detail-ref), so putting data-ref on this <tr> made one click BOTH open the
             roll-lengths modal AND collapse the deal underneath it. Ryan hit it immediately.
             On the cells, `closest('.rqd-roll-lengths-btn')` still resolves from any click inside
             the row while `closest('tr[data-ref]')` finds nothing — both cells carry it, so the
             whole line stays clickable. */
          /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-16) — INHERITED LENGTHS SAY SO ON HOVER.
             A swapped-in line has no cut list of its own and can never have one, so these were
             carried from the line it replaced. They are drawn identically because they describe
             the same material — but somebody reading them should be able to find out where they
             came from without opening the adjustment, so the tooltip says it rather than adding
             chrome to a row Ryan asked to look like its neighbour. */
          var _rollTip = it.rollCutsInherited
            ? 'Carried from the line this item replaced, which was the same material in another colour'
            : 'Edit the cut lengths';
          var _rollCellAttrs = _rollClickable
            ? ' class="rqd-roll-lengths-btn" data-ref="' + esc(ref) + '" title="' + esc(_rollTip) + '"'
            : (it.rollCutsInherited ? ' title="' + esc(_rollTip) + '"' : '');
          rows += '<tr class="rqd-online-items__roll-sub'
              + (_rollMoreAdded ? ' rqd-online-items__roll-sub--review' : '')
              + (_rollClickable ? ' rqd-online-items__roll-sub--clickable' : '') + '">' +
            '<td' + _rollCellAttrs + '>' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              '<span class="rqd-roll-layout">&#128208; Roll Layout: ' + _rollBody + '</span>' +
              rollMathLine(it, opts, ref) + // CUSTOM_ROLLMATH_BY_CLAUDE
              (_rollMoreAdded
                ? ' <span class="rqd-roll-layout-review">&#9888; More of this material was added since these lengths were set &mdash; re-cut before the PO goes out</span>'
                : '') +
            '</td>' +
            '<td colspan="' + (showCostCol ? 5 : 4) + '"' + _rollCellAttrs + '></td>' +
          '</tr>';
        } else if (it.isRoll && isOnline) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-25) — the missing-roll-layout
             nag is ONLINE-only. Online rolls come from the PDP calculator, which
             SHOULD carry a Roll Layout property — its absence is a real gap worth
             flagging. Offline rolls are rep-built (the rep supplies cut lengths as
             sub-text by hand), so a missing layout there isn't an error and the
             warning was just noise. The present-layout row above still shows for both. */
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — this row is clickable too now.
             Ryan remembered it working this way; it never did. It announced a missing layout and
             then offered nothing to do about it, so the fix lived on a different card you had to
             go and find. It is the state MOST in need of a one-click route to the editor, so it
             gets the same treatment as the amber row rather than a second interaction to learn. */
          var _missClickable = !!(opts && opts.isAdmin && ref);
          // Cells, not the <tr> — see the note on the layout row above (a tr[data-ref] inside the
          // detail panel makes the row-toggle handler collapse the deal on the same click).
          var _missCellAttrs = _missClickable
            ? ' class="rqd-roll-lengths-btn" data-ref="' + esc(ref) + '" title="Add the cut lengths"'
            : '';
          rows += '<tr class="rqd-online-items__roll-sub rqd-online-items__roll-sub--missing'
              + (_missClickable ? ' rqd-online-items__roll-sub--clickable' : '') + '">' +
            '<td colspan="' + (showCostCol ? 6 : 5) + '"' + _missCellAttrs + '>' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              '<span class="rqd-roll-layout-missing">&#9888; No roll layout on file — call the customer for roll lengths before sending the PO</span>' +
            '</td>' +
          '</tr>';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        /* CUSTOM_LEGENDSPEC_BY_CLAUDE (Ryan 2026-08-13) — the Legend build spec, exactly as the PO
           will print it, and editable in place.

           Shown because it was invisible: the factory reads "Frame Color / Pad Color / Metal Shroud
           Color" off the PO and the rep had no way to see those rows, let alone change them.

           Editable because of the options we deliberately DON'T model on the storefront. Two-tone
           upholstery — pad top one colour, pad side another — is about one order in a hundred, and
           putting it on the site would cost every other customer a decision they don't want. So the
           rep rewrites the spec on the one order it matters for: rename "Pad Color:" to
           "Pad Top Color:", Enter, add "Pad Side Color: …". Clearing reverts to derived.

           Admin-only, same gate as the SKU/qty/cost pencils. */
        /* CUSTOM_SPECMISMATCH_BY_CLAUDE (Ryan 2026-08-21) — the quote text and the variant the PO
           reads disagree. Rendered ABOVE the build spec, because the spec below is the wrong answer
           and this row is why.

           Shown to EVERY rep, not just admin: the build-spec pencil is an edit and stays gated, but
           this is a warning about the rep's own quote, and the rep is the one who knows which of the
           two documents is right. Gating a warning behind the ability to fix it is how RW-1284-A got
           to the factory — the affordance built to catch it rendered on a path that deal never took. */
        if (Array.isArray(it.specMismatches) && it.specMismatches.length) {
          var mmBody = it.specMismatches.map(function (m) {
            return esc(m.label) + ': quote says "' + esc(m.quoted) + '", order is built as "' + esc(m.variant) + '"';
          }).join(' &middot; ');
          rows += '<tr class="rqd-online-items__roll-sub rqd-online-items__roll-sub--missing">' +
            '<td colspan="' + (showCostCol ? 6 : 5) + '">' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              '<span class="rqd-spec-mismatch">&#9888; Quote and order disagree &mdash; ' + mmBody +
                '. The vendor PO prints what the ORDER says.</span>' +
            '</td>' +
          '</tr>';
        }
        /* END CUSTOM_SPECMISMATCH_BY_CLAUDE */
        /* CUSTOM_LEGENDSPEC_BY_CLAUDE (Ryan 2026-08-21) — gate on isAdmin, NOT showCostCol.
           showCostCol is `isAdmin && isOnline`, so for two years the build spec — the rows the
           factory paints from — rendered only on ONLINE orders, while nearly all Legend business is
           rep-built. The cost COLUMN is online-only for a real reason (an offline line's cost is
           editable from the Workstation); the build spec has no such reason, and its absence meant
           RW-1284-A's wrong colours had nowhere to show and no pencil to fix them. */
        if (Array.isArray(it.legendSpecRows) && it.legendSpecRows.length && !!(opts && opts.isAdmin)) {
          var specOverridden = !!it.legendSpecOverridden;
          // Joined with a SPACE, not '' — adjacent inline-blocks with no whitespace between them
          // cannot wrap, so a long spec would overflow its column instead of breaking between
          // rows. The comma comes from the ::after; the space is purely the break opportunity.
          var specBody = it.legendSpecRows.map(function (r) {
            return '<span class="rqd-legend-spec__row">' + esc(r) + '</span>';
          }).join(' ');
          rows += '<tr class="rqd-online-items__roll-sub rqd-online-items__spec-sub">' +
            '<td colspan="' + (showCostCol ? 6 : 5) + '">' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              '<span class="rqd-legend-spec rqd-line-legend-spec-edit' + (specOverridden ? ' rqd-legend-spec--overridden' : '') + '"' +
                ' data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId || '') + '"' +
                ' data-spec-note="' + esc(it.legendSpecNote || '') + '"' +
                ' data-spec-derived="' + esc((it.legendSpecDerived || []).join('\n')) + '"' +
                ' title="' + (specOverridden
                  ? 'Custom build spec — click to edit. Clear the box to revert to the colours from the order.'
                  : 'Build spec as the PO will print it. Click to rewrite — rename a row, or press Enter for a new one.') + '">' +
                '&#127912; Build spec' + (specOverridden ? ' <span class="rqd-legend-spec__flag">edited</span>' : '') + ': ' +
                '<span class="rqd-legend-spec-value">' + specBody + '</span> ' +
                '<span class="rqd-fin-edit-icon">&#9998;</span>' +
              '</span>' +
            '</td>' +
          '</tr>';
        }
        /* END CUSTOM_LEGENDSPEC_BY_CLAUDE */
        /* CUSTOM_LEGENDWS_BY_CLAUDE (Ryan 2026-08-13) — Stacks sub-row on a Legend weight-stack
           upgrade line. Free text ("Crossover 1, Crossover 2, Lat Pulldown 1"), because nothing
           in Shopify enumerates a machine's stacks and the multi-stack units are out of the
           storefront picker's v1 scope. It is NOT a note: the label count sets the PO's pound
           quantity (four stacks at +50 lb prints 200) and each label becomes a PO sub-row, so
           the row echoes the resulting pound figure to make that visible before sending.
           Admin-only, same gate as the SKU/qty/cost pencils. */
        if (it.isStackUpgrade && showCostCol) {
          var stackNote = it.stackUpgradeNote || '';
          var stackLbs = (it.stackUpgradeLbs != null) ? it.stackUpgradeLbs : null;
          var stackInner = stackNote
            ? esc(stackNote)
            : '<span class="rqd-cell-empty">— one stack (click to name several)</span>';
          var stackSuffix = (stackLbs != null)
            ? ' <span class="rqd-stack-upgrade__lbs">&rarr; ' + stackLbs + ' lb on the PO</span>'
            : '';
          rows += '<tr class="rqd-online-items__roll-sub">' +
            '<td colspan="' + (showCostCol ? 6 : 5) + '">' +
              '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
              // data-stack-note carries the RAW value so the editor round-trips it exactly —
              // the visible span holds either escaped HTML or the placeholder, neither of
              // which is safe to read back as the field's current value.
              '<span class="rqd-stack-upgrade rqd-line-stack-edit" data-ref="' + esc(ref) + '" data-line-item-id="' + esc(it.lineItemId || '') + '" data-stack-note="' + esc(stackNote) + '" title="Which stacks get the upgrade. Comma-separated; each one prints as its own PO line and adds its pounds to the PO quantity.">' +
                '&#127947; Stacks: <span class="rqd-line-stack-value">' + stackInner + '</span> ' +
                '<span class="rqd-fin-edit-icon">&#9998;</span>' + stackSuffix +
              '</span>' +
            '</td>' +
          '</tr>';
          if (it.stackUpgradeParentUnknown) {
            rows += '<tr class="rqd-online-items__roll-sub rqd-online-items__roll-sub--missing">' +
              '<td colspan="' + (showCostCol ? 6 : 5) + '">' +
                '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' +
                '<span class="rqd-roll-layout-missing">&#9888; Can&rsquo;t tell which machine this upgrades — the PO will print <code>' + esc(it.sku || 'WS-UPGRADE') + '</code>, which is not a Legend part number. Set the SKU to <code>{model}-WS</code> before sending.</span>' +
              '</td>' +
            '</tr>';
          }
        }
        /* END CUSTOM_LEGENDWS_BY_CLAUDE */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Bundle component
           sub-rows. When a parent line has bundleComponents, render one
           sub-row per component beneath the parent showing:
             • indented title ("↳ Oval Barrel")
             • component SKU
             • inherited qty (from the parent — non-editable)
             • blank Price (customer paid for the bundle as one item)
             • per-component Cost cell with ✎ pencil (editable per order)
             • blank Total
           Cost edits POST to /line-bundle-component-cost-override which
           writes wm.bundleComponentCostOverrides[lineItemId][componentSku].
           That feeds the parent's effectiveUnitCost (Σ of components),
           which feeds COGS on the Financials card AND the PO generator's
           per-component PO line cost. */
        if (Array.isArray(it.bundleComponents) && it.bundleComponents.length > 0) {
          it.bundleComponents.forEach(function (c) {
            var _ceffCost = (c.effectiveUnitCost != null) ? c.effectiveUnitCost : (c.baseUnitCost || 0);
            var _coverridden = !!c.costOverridden;
            var _compCostCell = '';
            if (showCostCol) {
              var _compEditIcon = '<span class="rqd-fin-edit-icon">&#9998;</span>';
              var _compOvClass = _coverridden ? ' rqd-line-cost-edit--overridden' : '';
              var _compTitle = _coverridden
                ? 'Override active — click to edit. Vendor default: ' + money(c.baseUnitCost || 0)
                : 'Click to override this component cost for this order only';
              _compCostCell = '<td class="rqd-line-cost-edit rqd-line-cost-edit--bundle-component' + _compOvClass + '"' +
                ' data-ref="' + esc(ref) + '"' +
                ' data-line-item-id="' + esc(it.lineItemId || '') + '"' +
                ' data-component-sku="' + esc(c.sku || '') + '"' +
                ' data-component-base-cost="' + (c.baseUnitCost || 0) + '"' +
                ' title="' + _compTitle + '">' +
                '<span class="rqd-line-cost-value">' + money(_ceffCost) + '</span> ' + _compEditIcon +
              '</td>';
            }
            var _compTitleHtml = '<span class="rqd-online-items__bundle-sub-icon">&#x21B3;</span> ' + esc(c.title || c.sku || '');
            rows += '<tr class="rqd-online-items__bundle-sub">' +
              '<td>' + _compTitleHtml + '</td>' +
              '<td>' + esc(c.sku || '—') + '</td>' +
              '<td>' + effectiveQty + '</td>' +
              '<td></td>' +
              _compCostCell +
              '<td></td>' +
              '</tr>';
          });
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      });

      var f = fees || {};
      var freight = parseFloat(f.freightCharged) || 0;
      var install = parseFloat(f.installCharged) || 0;
      var cardFee = parseFloat(f.cardFee) || 0;
      var taxes = parseFloat(f.taxes) || 0;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-02) — Order-level discount.
         Online line items render at their pre-code price (Shopify's
         discountedTotalSet); a discount CODE is applied at the order level and
         isn't reflected in the line totals. The gap between the line subtotal
         and the real goods revenue (f.directTotal, which DOES net the code) is
         the order discount. Surfaced on SHOP-1733: line $1,046.25 vs goods
         $994.50 → a $51.75 code with no row to explain it. Online only (offline
         directTotal semantics differ); ignore sub-cent rounding noise. */
      var _orderDiscount = isOnline
        ? Math.round((subtotal - (parseFloat(f.directTotal) || 0)) * 100) / 100
        : 0;
      if (_orderDiscount < 0.02) _orderDiscount = 0;
      var feeRows = '';
      function feeRow(label, amount, hasValue) {
        var amountHtml = hasValue ? money(amount) : '<span class="rqd-online-items__fee-empty">—</span>';
        return '<tr class="rqd-online-items__fee-row">' +
          '<td colspan="4" class="rqd-online-items__fee-label">' + label + '</td>' +
          '<td class="rqd-online-items__fee-amount">' + amountHtml + '</td>' +
        '</tr>';
      }
      // Offline: always render Shipping & Freight and Installation, even when
      // not yet entered, so the rep is reminded those values still need to
      // come in. Online: only show fees actually charged.
      // Card Processing renders only once it is a real charged dollar — see the
      // gate below the freight/install rows (2026-08-17). Before the order-paid
      // webhook reconciles it, f.cardFee is 0 and no row draws, which is the
      // same "don't show a fee we don't know was charged" guarantee the old
      // blanket omission gave.
      // CUSTOM_RFQPORTAL_BY_CLAUDE — Order-level discount row (negative), shown
      // first so items − discount = goods reconciles before freight/tax. Reuses
      // the credit-row styling.
      if (_orderDiscount > 0) {
        feeRows += '<tr class="rqd-online-items__fee-row rqd-online-items__fee-row--credit">' +
          '<td colspan="4" class="rqd-online-items__fee-label">Discount</td>' +
          '<td class="rqd-online-items__fee-amount rqd-online-items__fee-amount--negative">-' + money(_orderDiscount) + '</td>' +
        '</tr>';
      }
      if (isOnline) {
        if (freight > 0) feeRows += feeRow('Shipping &amp; Freight', freight, true);
      } else {
        feeRows += feeRow('Shipping &amp; Freight', freight, freight > 0);
        feeRows += feeRow('Installation', install, install > 0);
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-17) — Card Processing row.
         The "intentionally omitted" note above is about a fee we don't yet know
         was charged — and that case is already covered by the amount itself:
         f.cardFee stays 0 until the order-paid webhook reconciles a real "Card
         Processing Fee" line item (wm.cardFee.status → 'yes'). Once it IS a
         charged dollar it belongs on the card, because it is money in the Order
         Total with no row to explain it. Same $62.38 hole SHOP-1933 showed on
         the Financials card, one card over. Rendered positive (it is revenue,
         not a deduction) and skipped when the substitution path is already
         drawing its own per-child card rows below. */
      var _substCardLines = (opts && Array.isArray(opts.parentCardFeeLines)) ? opts.parentCardFeeLines : [];
      var _showCardFeeRow = cardFee > 0 && _substCardLines.length === 0;
      if (_showCardFeeRow) {
        feeRows += '<tr class="rqd-online-items__fee-row rqd-online-items__fee-row--cardfee">' +
          '<td colspan="4" class="rqd-online-items__fee-label">Card Processing</td>' +
          '<td class="rqd-online-items__fee-amount rqd-online-items__fee-amount--positive">+' + money(cardFee) + '</td>' +
        '</tr>';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Existing Credit row in Order Contents.
         When this quote has a credit applied (linked-child case), render
         a negative-amount row directly UNDER Shipping & Freight +
         Installation (and BEFORE Tax) so the math visibly reconciles
         (items + fees − credit + tax = Order Total). Without this, the
         Order Total appeared to "drop" with no in-card explanation. */
      var linkedCreditAmount = parseFloat(opts && opts.linkedCreditAmount) || 0;
      var linkedCreditSourceRef = (opts && opts.linkedCreditSourceRef) || '';
      if (linkedCreditAmount > 0) {
        var _ecLabel = 'Existing Credit'
          + (linkedCreditSourceRef ? ' <span class="rqd-online-items__fee-sublabel">(' + esc(linkedCreditSourceRef) + ')</span>' : '');
        feeRows += '<tr class="rqd-online-items__fee-row rqd-online-items__fee-row--credit">' +
          '<td colspan="4" class="rqd-online-items__fee-label">' + _ecLabel + '</td>' +
          '<td class="rqd-online-items__fee-amount rqd-online-items__fee-amount--negative">-' + money(linkedCreditAmount) + '</td>' +
        '</tr>';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Parent-side card processing rows.
         When the parent has adopted a child's items via substitution, the
         customer-charged cardFee on the child needs to surface here so the
         Order Contents math reconciles to the parent's own grandTotal:
           items + freight + install + tax + Σ(child cardFee)
             − Σ(child total)  =  parent's deposit ( = f.grandTotal )
         Positioned BEFORE the credit rows (positive fees first, then
         negative adjustment to land on Order Total). */
      var parentCardFeeLines = (opts && Array.isArray(opts.parentCardFeeLines)) ? opts.parentCardFeeLines : [];
      parentCardFeeLines.forEach(function(pf) {
        var _pfAmt = parseFloat(pf && pf.amount) || 0;
        if (_pfAmt <= 0) return;
        var _pfRef = (pf && pf.ref) ? String(pf.ref) : '';
        var _pfLabel = 'Card Processing'
          + (_pfRef ? ' <span class="rqd-online-items__fee-sublabel">(' + esc(_pfRef) + ')</span>' : '');
        feeRows += '<tr class="rqd-online-items__fee-row rqd-online-items__fee-row--cardfee">' +
          '<td colspan="4" class="rqd-online-items__fee-label">' + _pfLabel + '</td>' +
          '<td class="rqd-online-items__fee-amount rqd-online-items__fee-amount--positive">+' + money(_pfAmt) + '</td>' +
        '</tr>';
      });
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Parent-side mirror of the credit row.
         When this quote is a PARENT with linked balance children that
         carry Existing Credit (e.g. SHOP-1689 → RW-1126-A), render one
         negative-amount row per child referencing the child's ref. Same
         placement (under Shipping & Freight + Installation + Card
         Processing, before Tax) and same visual treatment as the
         child-side row above. */
      var parentCreditLines = (opts && Array.isArray(opts.parentCreditLines)) ? opts.parentCreditLines : [];
      parentCreditLines.forEach(function(pc) {
        var _pcAmt = parseFloat(pc && pc.amount) || 0;
        if (_pcAmt <= 0) return;
        var _pcRef = (pc && pc.ref) ? String(pc.ref) : '';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-18) — Label was
           "Existing Credit" which read as a discount. Renamed to
           "Paid on linked {ref}" so it's clear this row represents
           a sibling order's payment offsetting THIS order's total —
           not a price reduction. Reinforces that Order Contents
           reflects this Shopify order's books only, with the linked
           sibling shown as an explicit reconciling line. */
        var _pcLabel = 'Paid on linked'
          + (_pcRef ? ' <span class="rqd-online-items__fee-sublabel">(' + esc(_pcRef) + ')</span>' : '');
        feeRows += '<tr class="rqd-online-items__fee-row rqd-online-items__fee-row--credit">' +
          '<td colspan="4" class="rqd-online-items__fee-label">' + _pcLabel + '</td>' +
          '<td class="rqd-online-items__fee-amount rqd-online-items__fee-amount--negative">-' + money(_pcAmt) + '</td>' +
        '</tr>';
      });
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      if (taxes > 0) feeRows += feeRow('Tax', taxes, true);

      /* CUSTOM_RFQPORTAL_BY_CLAUDE — When any line has a qty override OR
         the parent has a linked balance freight allocation, Shopify's
         f.grandTotal reflects the parent-only Shopify total and lags the
         consolidated view shown elsewhere on the page (Balance Chain "Combined
         total"). Recompute from the corrected subtotal + freight (which
         already includes balanceFreightAllocation) so Order Total moves with
         the qty edit. Falls back to f.grandTotal for un-overridden, non-
         balance orders so legacy behavior is preserved. */
      var _hasAnyQtyOverride = items.some(function(it) { return !!it.quantityOverridden; });
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-02) — Also recompute when a
         freight balance has been collected (opts.rollUpBalanceTotal): freight
         includes the collected balance but Shopify's grandTotal is parent-only,
         so use subtotal + freight + install + tax to roll it up (SHOP-1732:
         $364.46 + $370.77 = $735.23 instead of the bare $367.47). */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-17) — the recompute branch must
         include the charged card fee, or it lands short of the deal's Amount by
         exactly that surcharge (SHOP-1933: $4,072.55 recomputed vs $4,134.93
         actual). The fallback branch needs no such term — Shopify's own
         f.grandTotal already has the card-fee line inside it; adding the row
         above is what makes THAT branch reconcile line-by-line. */
      var _cardFeeInTotal = _showCardFeeRow ? cardFee : 0;
      var grandTotal = (_hasAnyQtyOverride || (opts && opts.rollUpBalanceTotal))
        ? (subtotal - _orderDiscount + freight + install + taxes + _cardFeeInTotal)
        : (parseFloat(f.grandTotal) || (subtotal - _orderDiscount + freight + install + taxes + _cardFeeInTotal));

      // Dividing rows — full-width hr-styled <td>. Sits between items / fees
      // and between fees / grand total. If there are no fees the divider
      // collapses to a single line above the grand total.
      var totalCols = showCostCol ? 6 : 5;
      var grandLabelSpan = showCostCol ? 5 : 4;
      var feeLabelSpan = showCostCol ? 5 : 4;
      var dividerRow = '<tr class="rqd-online-items__divider"><td colspan="' + totalCols + '"></td></tr>';
      // Need to also rebuild feeRows with the right colspan when admin sees the cost column.
      // The feeRow helper above used colspan=4 hard-coded — patch the rendered HTML.
      if (showCostCol) {
        feeRows = feeRows.replace(/colspan="4" class="rqd-online-items__fee-label"/g, 'colspan="' + feeLabelSpan + '" class="rqd-online-items__fee-label"');
      }
      var feeBlock = feeRows
        ? dividerRow + feeRows + dividerRow
        : dividerRow;
      /* CUSTOM_DDMOBILE_BY_CLAUDE (Ryan 2026-10-29): on a phone the table swipes sideways, and the fee rows and the Order
         Total swiped away with it ("keep the numbers in sight the entire time ... then you're only swiping left/right
         on the top order contents rows"). The same rows again, as a fixed list under the table: each label beside its
         amount, the row's own classes kept so a credit stays red and a card fee green. dashboard.css shows this list
         and hides the in-table copies below 770px only; desktop keeps the table as it was. Built FROM feeRows, so the
         two can never list different fees. */
      var _ocTotals = '';
      feeRows.replace(/<tr class="([^"]*)"><td colspan="\d+" class="rqd-online-items__fee-label">([\s\S]*?)<\/td><td class="([^"]*)">([\s\S]*?)<\/td><\/tr>/g,
        function (_m, trCls, label, amtCls, amt) {
          _ocTotals += '<div class="rqd-oc-totals__row ' + trCls + '"><span class="rqd-online-items__fee-label">' + label + '</span>'
            + '<span class="' + amtCls + '">' + amt + '</span></div>';
          return _m;
        });
      _ocTotals = '<div class="rqd-oc-totals">' + _ocTotals
        + '<div class="rqd-oc-totals__row rqd-oc-totals__grand"><span>Order Total</span><span class="rqd-online-items__grand">' + money(grandTotal) + '</span></div></div>';

      // Wrap the table in a scrollable container so narrow mobile viewports
      // can swipe left/right to see SKU/Qty/Price/Total without truncating
      // line item names. Desktop has plenty of width — overflow stays auto
      // (no scrollbar shows when content fits).
      var costColHeader = showCostCol ? '<th class="rqd-online-items__cost">Cost</th>' : '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-18) — The standalone
         "Log dims" button that lived here has been retired. Pallet
         dims entry was promoted into the Fulfillment card (formerly
         Tracking) on offline-sold deals as a first-class step. */
      return '<div class="rqd-detail__arap">' +
        '<div class="rqd-detail__section-title">Order Contents</div>' +
        '<div class="rqd-online-items__scroll">' +
        '<table class="rqd-online-items"><thead><tr>' +
          '<th class="rqd-online-items__title">Item</th>' +
          '<th class="rqd-online-items__sku">SKU</th>' +
          '<th class="rqd-online-items__qty">Qty</th>' +
          '<th class="rqd-online-items__price">Price</th>' +
          costColHeader +
          '<th class="rqd-online-items__total">Total</th>' +
        '</tr></thead><tbody>' + rows + feeBlock + '</tbody><tfoot><tr>' +
          '<td colspan="' + grandLabelSpan + '" class="rqd-online-items__grand-label">Order Total</td>' +
          '<td class="rqd-online-items__grand">' + money(grandTotal) + '</td>' +
        '</tr></tfoot></table>' +
        '</div>' +
        _ocTotals +   // CUSTOM_DDMOBILE_BY_CLAUDE - the phone's fixed totals
        '</div>';
    }

    // === renderBalanceReconciliationCard — extracted from main.js:7146-7210 ===
    // Linked Balance Reconciliation card — only renders when the parent has
    // linked balance child orders (q.balanceChildren). Shows the running
    // ledger: child total → applied (qty deltas + freight allocation) →
    // remaining. The PO generator hard-blocks when remaining ≠ 0 so admin
    // can't accidentally PO a vendor before the full child amount has been
    // accounted for on the parent (qty corrections + freight bucket).
    //
    // The freight-allocation input is editable inline (blur/Enter saves) —
    // same UX as the per-line cost/qty overrides.
    function renderBalanceReconciliationCard(q, isAdmin) {
      if (!isAdmin) return '';
      if (!q || !Array.isArray(q.balanceChildren) || q.balanceChildren.length === 0) return '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Substitution mode skips the
         reconciliation card. When a balance has substitutesParent=true,
         the child's items + financials wholesale replace the parent's
         (deposit-balance pattern). The reconciliation ledger (allocate
         child $$ to parent qty deltas + freight bucket) makes no sense
         in that mode — there's no extra $$ to allocate; the parent's
         payment is the deposit, the child's payment is the balance,
         and the substitution just swaps what's being shipped. Hiding
         keeps the panel focused. */
      if (q.substitutesFromChildRef) return '';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var rec = q.balanceReconciliation || { childTotal: 0, appliedFromQty: 0, appliedFromFreight: 0, applied: 0, remaining: 0, balanced: false };
      var childRefs = q.balanceChildren.map(function (c) { return c.ref || c.orderName; }).filter(Boolean).join(', ');
      var pillCls, pillText;
      if (rec.balanced) {
        pillCls = 'rqd-balance-rec__pill--balanced';
        pillText = '&#10003; Balanced — ready for PO';
      } else if (rec.remaining > 0) {
        pillCls = 'rqd-balance-rec__pill--short';
        pillText = money(rec.remaining) + ' unallocated';
      } else {
        pillCls = 'rqd-balance-rec__pill--over';
        pillText = 'Over-applied by ' + money(Math.abs(rec.remaining));
      }
      var html = '';
      html += '<div class="rqd-detail__arap rqd-balance-rec">';
      // sectionTitleHtml injects the ✓ when done so the collapsed
      // header reads as "reconciliation complete" at a glance. The
      // applyCollapseState() pass post-render wires the click-to-
      // toggle behaviour and the shouldAutoCollapse(
      // 'linked-balance-reconciliation', q) branch returns true when
      // balanced — defaulting the card collapsed on next open.
      html += sectionTitleHtml('Linked Balance Reconciliation', !!rec.balanced);
      // Standard .rqd-detail__row label/value treatment — matches the
      // Financials + Sold To cards' visual rhythm (13px label muted, 13px
      // value 600-weight right-aligned tabular-nums). Last row gets the
      // grand-total bold separator treatment.
      html += '<div class="rqd-detail__row">' +
        '<span>Linked child total<span class="rqd-balance-rec__refs"> · ' + esc(childRefs) + '</span></span>' +
        '<span>' + money(rec.childTotal) + '</span>' +
      '</div>';
      html += '<div class="rqd-detail__row">' +
        '<span>Applied via qty adjustments</span>' +
        '<span>' + money(rec.appliedFromQty) + '</span>' +
      '</div>';
      html += '<div class="rqd-detail__row rqd-balance-rec__freight-row">' +
        '<span>Applied to freight</span>' +
        '<span class="rqd-balance-rec__freight-edit" data-ref="' + esc(q.reference) + '" title="Click to allocate the remaining child amount to freight (or clear)">' +
          '<span class="rqd-fin-edit-icon">&#9998;</span> ' +
          '<span class="rqd-balance-rec__freight-value">' + money(rec.appliedFromFreight) + '</span>' +
        '</span>' +
      '</div>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — added goods/fees row appears after a one-click
         auto-reconcile (the categorized non-freight remainder: new goods + card fee). */
      if (rec.appliedFromAddedGoods && rec.appliedFromAddedGoods > 0) {
        html += '<div class="rqd-detail__row">' +
          '<span>Applied to added goods / fees</span>' +
          '<span>' + money(rec.appliedFromAddedGoods) + '</span>' +
        '</div>';
      }
      html += '<div class="rqd-detail__row rqd-balance-rec__total-row">' +
        '<span>Remaining</span>' +
        '<span>' + money(rec.remaining) + '</span>' +
      '</div>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — one-click auto-reconcile / undo, per linked child,
         rendered on the SAME ROW as the status pill. Uses .rqd-invoice__btn so it inherits
         the SOLD card's forest-green / pale-green "do this next" button styling (shared with
         the Fulfillment / Tracking / Invoice cards). Itemized-child check happens server-side
         (clean error if the child has no spelled-out line items). */
      var _arRefs = q.autoReconciledChildRefs || [];
      var _arBtns = '';
      q.balanceChildren.forEach(function (c) {
        var cref = c.ref || c.orderName;
        if (!cref) return;
        if (_arRefs.indexOf(cref) >= 0) {
          _arBtns += '<button type="button" class="rqd-invoice__btn rqd-balance-rec__undo-btn" data-ref="' + esc(q.reference) + '" data-child-ref="' + esc(cref) + '" title="Undo auto-reconcile from ' + esc(cref) + '">Undo reconcile</button>';
        } else if (rec.remaining > 0.005) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-25) — tooltip describes what the
             click DOES (not just which child): allocates the child's goods at their real
             ordered quantity + freight + fees onto this order to balance the linked total,
             and a same-product add-on merges into the matching line on the vendor PO. */
          var _arTip = 'Auto-reconcile ' + cref + ' (' + money(c.amount) + '): allocates its goods (at their real ordered quantity), freight, and fees onto this order to balance the linked total. A same-product add-on merges into the matching line on the vendor PO.';
          _arBtns += '<button type="button" class="rqd-invoice__btn rqd-balance-rec__auto-btn" data-ref="' + esc(q.reference) + '" data-child-ref="' + esc(cref) + '" title="' + esc(_arTip) + '">Auto-reconcile</button>';
        }
      });
      html += '<div class="rqd-balance-rec__pill-wrap">' +
        '<span class="rqd-balance-rec__pill ' + pillCls + '">' + pillText + '</span>' +
        _arBtns +
      '</div>';
      html += '</div>';
      return html;
    }

    // === sectionTitleHtml — extracted from main.js:7220-7226 ===
    // Helper for section-title rendering with an optional green checkmark
    // when the card's work is complete. Mirrors the To Do column's
    // "✓ Fulfilled" treatment so the rep can scan "this card's done" at a
    // glance the same way they read fulfillment state on the row.
    function sectionTitleHtml(label, done) {
      var checkHtml = done
        ? '<span class="rqd-detail__section-done" title="All steps complete">&#10003;</span>'
        : '';
      return '<div class="rqd-detail__section-title' + (done ? ' rqd-detail__section-title--done' : '') + '">'
        + label + checkHtml + '</div>';
    }

    /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (AIOPS 9ab5bd14ca5f + be87acc13b79, Ryan 2026-09-26 - RW-1317-C) —
       "PURCHASING (3/3) ✓": a multi-vendor card says how many of its supplier rows are done, in the
       header's own type, with the tick AFTER the bracket. Same shape and same class as the Freight
       card's "FREIGHT (1/2) ✓" (titleWithSummary in dashboard-freight-cards.js), so the board reads
       as one set.
       ⛔ A CARD THAT USES THIS MUST DECLARE data-section-id. The collapse bookkeeping slugs a card's
       identity from its title text, so a live count in the title would give "purchasing-2-3" today
       and "purchasing-3-3" tomorrow and the card would forget it was collapsed.
       ⚠️ No count below two rows — "(1/1)" on a single-supplier deal is noise, and "(0/0)" reads as
       a bug. Below two this is exactly sectionTitleHtml. */
    function sectionTitleWithCount(label, done, doneCount, totalCount) {
      var base = sectionTitleHtml(label, done);
      if (!(totalCount > 1)) return base;
      var counter = '<span class="rqd-section-title__summary">(' + doneCount + '/' + totalCount + ')</span>';
      var at = base.indexOf('<span class="rqd-detail__section-done"');
      return at === -1 ? base.replace(/<\/div>$/, counter + '</div>') : base.slice(0, at) + counter + base.slice(at);
    }

  // === Init contract ===
  window.RQD.initOrderCards = function(deps) {
    esc                       = deps.esc;
    money                     = deps.money;
    getIsAdmin                = deps.getIsAdmin;
    buildOnlineFlowSteps      = deps.buildOnlineFlowSteps;
    isFreightBalanced         = deps.isFreightBalanced;
    vendorNamesMatch          = deps.vendorNamesMatch;
    collapsedSections         = deps.collapsedSections;
    sectionsAutoCollapsedRef  = deps.sectionsAutoCollapsedRef;
    manuallyToggledSections   = deps.manuallyToggledSections || {};
  };

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — open one card from outside this module.
     Lives HERE because `collapsedSections` is this module's store: a caller that just stripped the
     --collapsed class itself would be overruled the moment applyCollapseState ran again on the next
     quiet render, and the bug would look intermittent. Clearing the stored flag is what makes it
     stick. Callers own the "only once" decision — this function has no memory on purpose, so it can
     also serve a future explicit "expand all". */
  function expandSection(ref, sectionId, scopeEl) {
    if (!ref || !sectionId) return false;
    if (collapsedSections[ref]) delete collapsedSections[ref][sectionId];
    var root = scopeEl || document;
    var card = (root.classList && root.classList.contains('rqd-detail__arap'))
      ? root
      : root.querySelector('.rqd-detail__arap[data-section="' + sectionId + '"]');
    if (!card) return false;
    card.classList.remove('rqd-detail__arap--collapsed');
    var chevron = card.querySelector('.rqd-arap__chevron');
    if (chevron) chevron.textContent = '−';
    return true;
  }

  // === Public API ===
  window.RQD.isOrderFullyFulfilled          = isOrderFullyFulfilled;
  window.RQD.applyCollapseState             = applyCollapseState;
  window.RQD.expandSection                  = expandSection;
  window.RQD.renderOrderContentsCard        = renderOrderContentsCard;
  window.RQD.renderBalanceReconciliationCard = renderBalanceReconciliationCard;
  window.RQD.sectionTitleHtml               = sectionTitleHtml;
  window.RQD.sectionTitleWithCount          = sectionTitleWithCount; // CUSTOM_DDCARDCOUNT_BY_CLAUDE
})();
